import React, { useState, useEffect } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import CustomModal from '../CustomModal/CustomModal'
import { createSpaceAsync, showCreateSpaceModal } from '../../Manage/actions'
import styles from './CreateSpaceModal.module.css'

const CreateSpaceModal = ({ onSpaceCreated }) => {
  const dispatch = useDispatch()
  const { showCreateSpaceModal: show, actionProcessing, spaces, members } = useSelector((state) => state.manage)
  const hasTeam = (members || []).length > 0

  const [spaceName, setSpaceName] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const [error, setError] = useState('')
  const SPACE_LIMIT = 100
  const hasReachedLimit = (spaces || []).length >= SPACE_LIMIT
  const trimmedName = spaceName.trim()

  useEffect(() => {
    if (!show) {
      setSpaceName('')
      setIsPrivate(false)
      setError('')
    }
  }, [show])

  const handleClose = () => {
    setSpaceName('')
    setError('')
    dispatch(showCreateSpaceModal(false))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    // Validation
    if (!trimmedName) {
      setError('Space name is required')
      return
    }

    if (trimmedName.length > 25) {
      setError('Space name cannot exceed 25 characters')
      return
    }

    if (hasReachedLimit) {
      setError(`You can only create up to ${SPACE_LIMIT} spaces`)
      return
    }

    dispatch(
      createSpaceAsync({ name: trimmedName, isPrivate: hasTeam && isPrivate }, (newSpace) => {
        if (onSpaceCreated && newSpace) {
          onSpaceCreated(newSpace)
        }
      })
    ).catch((error) => {
      // Error is handled by the action creator
      const errorMessage = error.response?.data?.code || error.code || error.message || 'Failed to create space'
      setError(errorMessage)
    })
  }

  const handleNameChange = (e) => {
    const value = e.target.value
    setSpaceName(value)

    if (error) {
      setError('')
    }
  }

  const remainingChars = 25 - spaceName.length
  const isOverLimit = spaceName.length > 25

  return (
    <CustomModal show={show} onHide={handleClose} centered size="md" enforceFocus={false}>
      <div className="p-4">
        <Modal.Header closeButton className={styles.header}>
          <Modal.Title className={styles.title}>Create a new Space</Modal.Title>
        </Modal.Header>

        <Modal.Body className={styles.body}>
          <p className={styles.subtitle}>Creating a new Space helps you organise your Projects.</p>

          {hasReachedLimit && (
            <div className={styles.limitMessage}>
              <p className={styles.limitText}>
                You&apos;ve reached the maximum of {SPACE_LIMIT} spaces. Delete an existing space to create a new one.
              </p>
            </div>
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Group className={styles.formGroup}>
              <Form.Label className={styles.label}>Space name</Form.Label>
              <Form.Control
                type="text"
                placeholder={hasReachedLimit ? 'Space limit reached' : 'E.g. Personal'}
                value={spaceName}
                onChange={handleNameChange}
                className={`${styles.input} ${error || isOverLimit ? styles.inputError : ''}`}
                disabled={actionProcessing || hasReachedLimit}
                maxLength={30}
              />
              <div className={styles.inputFooter}>
                {error && <span className={styles.errorText}>{error}</span>}
                <span className={`${styles.charCount} ${isOverLimit ? styles.charCountError : ''}`}>
                  {remainingChars} characters remaining
                </span>
              </div>
            </Form.Group>
            {hasTeam && (
              <Form.Check
                type="checkbox"
                id="create-space-private"
                className={styles.privateCheck}
                label="Make this space private (only you and the team owner can see it)"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                disabled={actionProcessing || hasReachedLimit}
              />
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer className={styles.footer}>
          <Button variant="secondary" onClick={handleClose} disabled={actionProcessing} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={actionProcessing || !trimmedName || isOverLimit || hasReachedLimit}
            className={styles.createButton}
          >
            {actionProcessing ? 'Creating...' : 'Create Space'}
          </Button>
        </Modal.Footer>
      </div>
    </CustomModal>
  )
}

export default CreateSpaceModal
